import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, CalendarDays, Clock3, Download, Search } from "lucide-react";
import AdminLayout from "./AdminLayout";
import AdminUserLink from "../components/AdminUserLink";
import API from "../services/api";

function AdminStarlineStakeAnalysis() {
  const [selectedDate, setSelectedDate] = useState(getIndiaDateValue());
  const [summary, setSummary] = useState({});
  const [markets, setMarkets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedNumberKey, setExpandedNumberKey] = useState("");

  const loadSummary = useCallback(async () => {
    try {
      const res = await API.get(`/admin/starline-stake?date=${selectedDate}`);
      setSummary(res.data?.summary || {});
      setMarkets(Array.isArray(res.data?.markets) ? res.data.markets : []);
    } catch {
      setSummary({});
      setMarkets([]);
    }
  }, [selectedDate]);

  const loadDetail = useCallback(async () => {
    if (!selected?.marketId || !selected?.timeSlot) return;
    try {
      const res = await API.get(`/admin/starline-stake/market/${selected.marketId}?date=${selectedDate}&time_slot=${encodeURIComponent(selected.timeSlot)}`);
      setDetail(res.data || null);
    } catch {
      setDetail(null);
    }
  }, [selected, selectedDate]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const groups = detail?.groups || [];
    if (!term) return groups;
    return groups.map((group) => ({
      ...group,
      numbers: group.numbers.filter((item) => String(item.number).toLowerCase().includes(term)),
    }));
  }, [detail, search]);

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Starline - Stake Analysis</h1>
            <p style={subtitle}>Time-wise betting analytics for single, SP, DP and TP</p>
          </div>
          <div style={dateWrap}>
            <CalendarDays size={18} />
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={dateInput} />
          </div>
        </div>

        <div style={summaryGrid}>
          <Summary label="Total Stake" value={money(summary.total_stake)} />
          <Summary label="Total Bets" value={Number(summary.total_bets || 0).toLocaleString("en-IN")} />
          <Summary label="Markets" value={summary.total_markets || 0} />
          <Summary label="Time Slots" value={summary.total_slots || 0} />
        </div>

        {!detail && (
          <div style={slotCard}>
            {markets.map((market) => (
              <section key={market.id}>
                <div style={slotHeader}>
                  <span style={badge}>{initials(market.name)}</span>
                  <div>
                    <strong>{market.name}</strong>
                    <small>Select time slot to view bids</small>
                  </div>
                </div>
                <div style={slotGrid}>
                  {market.slots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      style={slotButton}
                      onClick={() => {
                        setDetail(null);
                        setSelected({ marketId: market.id, timeSlot: slot.time_slot });
                      }}
                    >
                      <Clock3 size={18} />
                      <b>{slot.time_slot}</b>
                      <small>{money(slot.stake)} | {slot.bets} bets</small>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {detail && (
          <>
            <section style={detailHeader}>
              <button type="button" style={backButton} onClick={() => setDetail(null)}>
                <ArrowLeft size={20} />
              </button>
              <span style={detailBadge}>{initials(detail.market?.name)}</span>
              <div style={{ flex: 1 }}>
                <strong>{detail.market?.name}</strong>
                <small>{detail.time_slot} - {selectedDate.split("-").reverse().join("/")}</small>
              </div>
              <div style={searchBox}>
                <Search size={16} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search number..." style={searchInput} />
              </div>
              <button type="button" style={exportButton}><Download size={16} /> Export</button>
            </section>

            <div style={resultStrip}>
              <span>Result: <b>{detail.result || "***"}</b></span>
              <span>Winners: <b>{detail.winners || 0}</b></span>
              <span>Payout: <b>{money(detail.total_payout)}</b></span>
            </div>

            {filteredGroups.map((group) => {
              const selectedNumber = group.numbers.find((item) => `${group.key}-${item.number}` === expandedNumberKey);
              return (
                <section key={group.key} style={groupCard}>
                  <div style={groupTop}>
                    <strong>{group.label}</strong>
                    <div style={groupStats}>
                      <span>Stake: <b>{money(group.stake)}</b></span>
                      <span>Bets: <b>{group.bets}</b></span>
                      <span>Active: <b>{group.active}/{group.total_slots || "-"}</b></span>
                    </div>
                  </div>
                  <div style={numberGrid}>
                    {group.numbers.map((item, index) => (
                      <button
                        key={`${group.key}-${item.number}`}
                        type="button"
                        onClick={() => {
                          const key = `${group.key}-${item.number}`;
                          setExpandedNumberKey((current) => current === key ? "" : key);
                        }}
                        style={{
                          ...numberCell,
                          ...(index === 0 ? gold : {}),
                          ...(index === 1 ? gray : {}),
                          ...(index === 2 ? orange : {}),
                          ...(expandedNumberKey === `${group.key}-${item.number}` ? activeNumberCell : {}),
                        }}
                      >
                        <span style={bubble}>{item.bets}</span>
                        <b>{item.number}</b>
                        <small>{money(item.stake)}</small>
                      </button>
                    ))}
                    {!group.numbers.length && <div style={empty}>No stake in this group</div>}
                  </div>
                  {selectedNumber && (
                    <div style={userBreakdownPanel}>
                      <div style={userBreakdownHeader}>
                        <strong>Users on {selectedNumber.number}</strong>
                        <span>{selectedNumber.users?.length || 0} users / {money(selectedNumber.stake)}</span>
                      </div>
                      <div style={userBreakdownList}>
                        {(selectedNumber.users || []).map((user) => (
                          <div key={`${group.key}-${selectedNumber.number}-${user.user_id}`} style={userBreakdownRow}>
                            <AdminUserLink userId={user.user_id} name={user.user_name || `User ${user.user_id}`} phone={user.user_phone || "-"} />
                            <span>{user.bets} bets</span>
                            <b>{money(user.stake)}</b>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </>
        )}
      </section>
    </AdminLayout>
  );
}

function Summary({ label, value }) {
  return <article style={summaryBox}><b>{value}</b><span>{label}</span></article>;
}

const money = (value) => {
  const amount = Number(value || 0);
  if (amount >= 1000) return `Rs ${(amount / 1000).toFixed(1)}K`;
  return `Rs ${amount}`;
};
const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "SL";

const page = { display: "grid", gap: 18 };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const title = { margin: 0, fontSize: 22, fontWeight: 900 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: 13 };
const dateWrap = { height: 44, display: "flex", alignItems: "center", gap: 10, border: "1px solid #cbd5e1", background: "#fff", padding: "0 12px" };
const dateInput = { border: 0, outline: 0, fontWeight: 800, fontSize: 15 };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 };
const summaryBox = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 18, display: "grid", gap: 6 };
const slotCard = { background: "#fff", border: "1px solid #dcfce7", borderRadius: 14, overflow: "hidden" };
const slotHeader = { minHeight: 74, background: "linear-gradient(90deg,#16a34a,#15803d)", color: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "0 18px" };
const badge = { width: 42, height: 42, display: "grid", placeItems: "center", background: "rgba(255,255,255,.2)", fontWeight: 900 };
const slotGrid = { display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 10, padding: 10 };
const slotButton = { minHeight: 74, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#15803d", display: "grid", placeItems: "center", gap: 3, cursor: "pointer" };
const detailHeader = { minHeight: 82, background: "linear-gradient(90deg,#16a34a,#15803d)", color: "#fff", borderRadius: 14, display: "flex", alignItems: "center", gap: 12, padding: "0 14px" };
const backButton = { width: 42, height: 42, border: 0, background: "rgba(255,255,255,.1)", color: "#fff", display: "grid", placeItems: "center", cursor: "pointer" };
const detailBadge = { ...badge, width: 48, height: 48 };
const searchBox = { height: 48, width: 220, background: "#fff", color: "#64748b", display: "flex", alignItems: "center", gap: 10, padding: "0 12px" };
const searchInput = { border: 0, outline: 0, width: "100%" };
const exportButton = { height: 46, border: 0, background: "rgba(255,255,255,.16)", color: "#fff", padding: "0 16px", display: "flex", alignItems: "center", gap: 8, fontWeight: 800 };
const resultStrip = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, minHeight: 50, display: "flex", alignItems: "center", gap: 28, padding: "0 18px" };
const groupCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" };
const groupTop = { height: 54, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", borderBottom: "1px solid #e5e7eb" };
const groupStats = { display: "flex", gap: 20, color: "#475569", fontSize: 13 };
const numberGrid = { display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 8, padding: 16 };
const numberCell = { minHeight: 78, border: "1px solid #d1d5db", background: "#fff", position: "relative", display: "grid", placeItems: "center", alignContent: "center", gap: 6, cursor: "pointer", color: "#0f172a" };
const activeNumberCell = { outline: "3px solid rgba(37,99,235,.28)", borderColor: "#16a34a", transform: "translateY(-1px)" };
const bubble = { position: "absolute", top: 6, right: 6, background: "#dcfce7", color: "#16a34a", borderRadius: 99, minWidth: 20, height: 20, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800 };
const gold = { background: "linear-gradient(180deg,#fbbf24,#f59e0b)", color: "#fff" };
const gray = { background: "linear-gradient(180deg,#9ca3af,#6b7280)", color: "#fff" };
const orange = { background: "linear-gradient(180deg,#fb923c,#ef4444)", color: "#fff" };
const empty = { gridColumn: "1 / -1", padding: 24, color: "#94a3b8", textAlign: "center" };
const userBreakdownPanel = { margin: "0 16px 16px", border: "1px solid #bbf7d0", borderRadius: 14, background: "#f0fdf4", overflow: "hidden" };
const userBreakdownHeader = { minHeight: 48, padding: "0 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, color: "#15803d", borderBottom: "1px solid #bbf7d0", flexWrap: "wrap" };
const userBreakdownList = { display: "grid", gap: 8, padding: 12 };
const userBreakdownRow = { display: "grid", gridTemplateColumns: "1fr .5fr .7fr", gap: 10, alignItems: "center", background: "#fff", border: "1px solid #dcfce7", borderRadius: 12, padding: "10px 12px", color: "#0f172a" };

export default AdminStarlineStakeAnalysis;

function getIndiaDateValue() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
  });
  return formatter.format(new Date());
}
